/**
 * AdminFAQsPage — Owner → Category → Entry drill-down management.
 *
 * Route: /oneadvanced-admin/faqs
 *
 * Navigation hierarchy:
 *   Sections list → Categories in owner  →  Entries in category
 *   Breadcrumb: [Admin Hub] / [FAQs] / [Owner] / [Category]
 *
 * Sections and categories support sort_order-based reordering via up/down arrows.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';

import { Page, Header, Content } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { oneadvancedApiRef } from '../../../../api/OneAdvancedApi';
import type {
  FaqSection,
  FaqCategory,
  FaqEntry,
  FeedbackSummary,
  FeedbackVoters,
  CreateFaqSectionInput,
  CreateFaqCategoryInput,
  CreateFaqEntryInput,
  UpdateFaqEntryInput,
} from '../../types';
import { SectionForm } from './SectionForm';
import { CategoryForm } from './CategoryForm';
import { EntryForm } from './EntryForm';

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 4,
    marginBottom: theme.spacing(2.5),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  bcLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': { textDecoration: 'underline' },
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontSize: 13,
  },
  bcCurrent: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  bcSep: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  levelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  levelTitle: { fontWeight: 700, fontSize: 18 },
  listRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  openBtn: {
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
    whiteSpace: 'nowrap' as const,
    fontWeight: 600,
  },
  rowContent: { flex: 1, minWidth: 0 },
  rowName: {
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  colorSwatch: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
    border: '1px solid rgba(0,0,0,0.15)',
  },
  arrowGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0,
    marginRight: theme.spacing(0.5),
  },
  arrowBtn: { padding: 2 },
  rowNumber: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    opacity: 0.5,
    minWidth: 22,
    textAlign: 'right' as const,
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.5px',
  },
}));

type Level = 'sections' | 'categories' | 'entries';

// ── Main component ────────────────────────────────────────────────────────────

export function AdminFAQsPage() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const api = useApi(oneadvancedApiRef);

  const [level, setLevel] = useState<Level>('sections');
  const [selectedSection, setSelectedSection] = useState<FaqSection | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<FaqCategory | null>(null);

  const [sections, setSections] = useState<FaqSection[]>([]);
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [entries, setEntries] = useState<FaqEntry[]>([]);
  const [entryFeedback, setEntryFeedback] = useState<Record<string, FeedbackSummary>>({});

  const [catCounts, setCatCounts] = useState<Record<string, number>>({});
  const [entryCounts, setEntryCounts] = useState<Record<string, number>>({});

  const [loading, setLoading] = useState(true);

  // Form state
  const [sectionFormOpen, setSectionFormOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<FaqSection | null>(null);
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FaqCategory | null>(null);
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FaqEntry | null>(null);

  // Delete confirm
  const [deleteDialog, setDeleteDialog] = useState<{
    type: 'section' | 'category' | 'entry';
    id: string;
    name: string;
    warning?: string;
  } | null>(null);

  // Feedback voters dialog
  const [votersDialog, setVotersDialog] = useState<{ entryId: string; question: string } | null>(null);
  const [voters, setVoters] = useState<FeedbackVoters | null>(null);
  const [votersLoading, setVotersLoading] = useState(false);

  // ── Load data ─────────────────────────────────────────────────────────────────

  const loadSections = useCallback(async () => {
    setLoading(true);
    const sectionList = await api.faq.getSections();
    setSections(sectionList);

    // Fetch all categories in parallel (one request per section, not sequential)
    const catResults = await Promise.all(
      sectionList.map(o => api.faq.getCategories(o.id)),
    );
    const allCats = catResults.flat();

    const cc: Record<string, number> = {};
    const ec: Record<string, number> = {};
    for (const o of sectionList) {
      cc[o.id] = allCats.filter(c => c.sectionId === o.id).length;
    }
    // entry_count is returned by GET /sections/:id/categories via the server-side
    // aggregation — no need to download all entries just for counting.
    for (const cat of allCats) {
      ec[cat.id] = cat.entryCount ?? 0;
    }
    setCatCounts(cc);
    setEntryCounts(ec);
    setLoading(false);
  }, [api]);

  const loadCategories = useCallback(async (section: FaqSection) => {
    setLoading(true);
    const cats = await api.faq.getCategories(section.id);
    setCategories(cats);
    // Use the server-side entry_count rather than downloading all entries.
    const ec: Record<string, number> = {};
    for (const cat of cats) {
      ec[cat.id] = cat.entryCount ?? 0;
    }
    setEntryCounts(prev => ({ ...prev, ...ec }));
    setLoading(false);
  }, [api]);

  const loadEntries = useCallback(async () => {
    if (!selectedCategory) return;
    setLoading(true);
    // Fetch only this category's entries (with full answer text) instead of
    // downloading every entry across all categories and filtering client-side.
    const filtered = await api.faq.getAdminEntriesByCategory(selectedCategory.id);
    setEntries(filtered);
    // Load feedback counts for all entries in parallel
    const fbResults = await Promise.allSettled(filtered.map(e => api.faq.getFeedback(e.id)));
    const fbMap: Record<string, FeedbackSummary> = {};
    fbResults.forEach((result, i) => {
      if (result.status === 'fulfilled') fbMap[filtered[i].id] = result.value;
    });
    setEntryFeedback(fbMap);
    setLoading(false);
  }, [api, selectedCategory]);

  useEffect(() => {
    if (level === 'sections') loadSections();
  }, [level, loadSections]);

  useEffect(() => {
    if (level === 'categories' && selectedSection) loadCategories(selectedSection);
  }, [level, selectedSection, loadCategories]);

  useEffect(() => {
    if (level === 'entries' && selectedCategory) loadEntries();
  }, [level, selectedCategory, loadEntries]);

  // ── Reorder helpers ───────────────────────────────────────────────────────────

  const moveSection = async (index: number, direction: 'up' | 'down') => {
    const newSections = [...sections];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newSections.length) return;
    [newSections[index], newSections[swapIdx]] = [newSections[swapIdx], newSections[index]];
    const reordered = newSections.map((o, i) => ({ ...o, sortOrder: i }));
    setSections(reordered);
    await api.faq.reorderSections(reordered.map(o => ({ id: o.id, sortOrder: o.sortOrder })));
  };

  const moveCategory = async (index: number, direction: 'up' | 'down') => {
    if (!selectedSection) return;
    const newCats = [...categories];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newCats.length) return;
    [newCats[index], newCats[swapIdx]] = [newCats[swapIdx], newCats[index]];
    const reordered = newCats.map((c, i) => ({ ...c, sortOrder: i }));
    setCategories(reordered);
    await api.faq.reorderCategories(
      selectedSection.id,
      reordered.map(c => ({ id: c.id, sortOrder: c.sortOrder })),
    );
  };

  // ── Navigation ────────────────────────────────────────────────────────────────

  const drillToCategories = (section: FaqSection) => {
    setSelectedSection(section);
    setLevel('categories');
  };

  const drillToEntries = (category: FaqCategory) => {
    setSelectedCategory(category);
    setLevel('entries');
  };

  const backToSections = () => {
    setLevel('sections');
    setSelectedSection(null);
    setSelectedCategory(null);
  };

  const backToCategories = () => {
    setLevel('categories');
    setSelectedCategory(null);
  };

  // ── Save handlers ─────────────────────────────────────────────────────────────

  const handleSaveSection = async (data: CreateFaqSectionInput) => {
    if (editingSection) {
      await api.faq.updateSection(editingSection.id, data);
    } else {
      await api.faq.createSection(data);
    }
    await loadSections();
  };

  const handleSaveCategory = async (data: CreateFaqCategoryInput) => {
    if (!selectedSection) return;
    if (editingCategory) {
      await api.faq.updateCategory(selectedSection.id, editingCategory.id, data);
    } else {
      await api.faq.createCategory(selectedSection.id, data);
    }
    await loadCategories(selectedSection);
  };

  const handleSaveEntry = async (data: CreateFaqEntryInput | UpdateFaqEntryInput) => {
    if (!selectedCategory) return;
    if (editingEntry) {
      await api.faq.updateEntry(editingEntry.id, data as UpdateFaqEntryInput);
    } else {
      await api.faq.createEntry(selectedCategory.id, data as CreateFaqEntryInput);
    }
    await loadEntries();
  };

  // ── Delete ────────────────────────────────────────────────────────────────────

  const confirmDelete = async () => {
    if (!deleteDialog) return;
    const { type, id } = deleteDialog;
    if (type === 'section') {
      await api.faq.deleteSection(id);
      await loadSections();
    } else if (type === 'category' && selectedSection) {
      await api.faq.deleteCategory(selectedSection.id, id);
      await loadCategories(selectedSection);
    } else if (type === 'entry') {
      await api.faq.deleteEntry(id);
      await loadEntries();
    }
    setDeleteDialog(null);
  };

  // ── Breadcrumbs ───────────────────────────────────────────────────────────────

  const Breadcrumbs = () => (
    <div className={classes.breadcrumbs}>
      <button type="button" className={classes.bcLink} onClick={() => navigate('/oneadvanced-admin')}>
        <HomeIcon style={{ fontSize: 14 }} /> Admin Hub
      </button>
      <span className={classes.bcSep}><ChevronRightIcon style={{ fontSize: 14 }} /></span>
      {level === 'sections' ? (
        <span className={classes.bcCurrent}>FAQs</span>
      ) : (
        <button type="button" className={classes.bcLink} onClick={backToSections}>FAQs</button>
      )}
      {(level === 'categories' || level === 'entries') && selectedSection && (
        <>
          <span className={classes.bcSep}><ChevronRightIcon style={{ fontSize: 14 }} /></span>
          {level === 'categories' ? (
            <span className={classes.bcCurrent}>{selectedSection.name}</span>
          ) : (
            <button type="button" className={classes.bcLink} onClick={backToCategories}>{selectedSection.name}</button>
          )}
        </>
      )}
      {level === 'entries' && selectedCategory && (
        <>
          <span className={classes.bcSep}><ChevronRightIcon style={{ fontSize: 14 }} /></span>
          <span className={classes.bcCurrent}>{selectedCategory.name}</span>
        </>
      )}
    </div>
  );

  // ── Level content ─────────────────────────────────────────────────────────────

  const renderLevelContent = () => {
    if (level === 'sections') {
      return (
        <>
          <div className={classes.levelHeader}>
            <Box>
              <Typography className={classes.levelTitle}>Sections</Typography>
              <Typography variant="body2" color="textSecondary">
                Use the arrows to reorder how sections appear in the FAQs sidebar.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => { setEditingSection(null); setSectionFormOpen(true); }}
            >
              Add section
            </Button>
          </div>
          <Paper variant="outlined">
            {sections.map((section, idx) => (
              <div key={section.id} className={classes.listRow}>
                <span className={classes.rowNumber}>{idx + 1}</span>
                <div className={classes.arrowGroup}>
                  <IconButton
                    className={classes.arrowBtn}
                    size="small"
                    disabled={idx === 0}
                    onClick={() => moveSection(idx, 'up')}
                    aria-label="Move up"
                  >
                    <ArrowUpwardIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    className={classes.arrowBtn}
                    size="small"
                    disabled={idx === sections.length - 1}
                    onClick={() => moveSection(idx, 'down')}
                    aria-label="Move down"
                  >
                    <ArrowDownwardIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </div>
                <div className={classes.colorSwatch} style={{ backgroundColor: section.color }} />
                <div className={classes.rowContent}>
                  <div className={classes.rowName}>{section.name}</div>
                  <div className={classes.rowMeta}>
                    {catCounts[section.id] ?? 0} categories
                  </div>
                </div>
                <div className={classes.rowActions}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setEditingSection(section); setSectionFormOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({
                        type: 'section', id: section.id, name: section.name,
                        warning: 'All categories and entries under it will also be deleted.',
                      })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <Button className={classes.openBtn} size="small" onClick={() => drillToCategories(section)}>
                  Categories <ChevronRightIcon fontSize="small" />
                </Button>
              </div>
            ))}
            {sections.length === 0 && (
              <Box p={3} textAlign="center" color="text.secondary">No sections yet. Add one to get started.</Box>
            )}
          </Paper>
        </>
      );
    }

    if (level === 'categories' && selectedSection) {
      return (
        <>
          <div className={classes.levelHeader}>
            <Box>
              <Typography className={classes.levelTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: selectedSection.color,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                {selectedSection.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Use the arrows to reorder how categories appear in the FAQs sidebar.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => { setEditingCategory(null); setCategoryFormOpen(true); }}
            >
              Add category
            </Button>
          </div>
          <Paper variant="outlined">
            {categories.map((cat, idx) => (
              <div key={cat.id} className={classes.listRow}>
                <span className={classes.rowNumber}>{idx + 1}</span>
                <div className={classes.arrowGroup}>
                  <IconButton
                    className={classes.arrowBtn}
                    size="small"
                    disabled={idx === 0}
                    onClick={() => moveCategory(idx, 'up')}
                    aria-label="Move up"
                  >
                    <ArrowUpwardIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    className={classes.arrowBtn}
                    size="small"
                    disabled={idx === categories.length - 1}
                    onClick={() => moveCategory(idx, 'down')}
                    aria-label="Move down"
                  >
                    <ArrowDownwardIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </div>
                <div className={classes.rowContent}>
                  <div className={classes.rowName}>{cat.name}</div>
                  <div className={classes.rowMeta}>{entryCounts[cat.id] ?? 0} entries</div>
                </div>
                <div className={classes.rowActions}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setEditingCategory(cat); setCategoryFormOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({
                        type: 'category', id: cat.id, name: cat.name,
                        warning: `All FAQ entries in this category (${entryCounts[cat.id] ?? 0}) will also be deleted.`,
                      })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <Button className={classes.openBtn} size="small" onClick={() => drillToEntries(cat)}>
                  Entries <ChevronRightIcon fontSize="small" />
                </Button>
              </div>
            ))}
            {categories.length === 0 && (
              <Box p={3} textAlign="center" color="text.secondary">No categories yet.</Box>
            )}
          </Paper>
        </>
      );
    }

    if (level === 'entries' && selectedCategory) {
      return (
        <>
          <div className={classes.levelHeader}>
            <Typography className={classes.levelTitle}>{selectedCategory.name}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => { setEditingEntry(null); setEntryFormOpen(true); }}
            >
              Add FAQ entry
            </Button>
          </div>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Feedback</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(entry => (
                  <TableRow key={entry.id} hover>
                    <TableCell style={{ maxWidth: 420 }}>
                      <Typography variant="body2" noWrap title={entry.question}>
                        {entry.question.length > 80 ? `${entry.question.substring(0, 80)}…` : entry.question}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status}
                        size="small"
                        color={entry.status === 'published' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5} sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        <Tooltip title="See who voted helpful">
                          <Box
                            display="flex" alignItems="center" gap={0.5}
                            sx={{ cursor: 'pointer', '&:hover': { color: '#16a34a' } }}
                            onClick={() => {
                              setVoters(null);
                              setVotersDialog({ entryId: entry.id, question: entry.question });
                              setVotersLoading(true);
                              api.faq.getFeedbackVoters(entry.id)
                                .then(v => setVoters(v))
                                .finally(() => setVotersLoading(false));
                            }}
                          >
                            <ThumbUpAltOutlinedIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                            {entryFeedback[entry.id]?.helpful ?? 0}
                          </Box>
                        </Tooltip>
                        <Tooltip title="See who voted not helpful">
                          <Box
                            display="flex" alignItems="center" gap={0.5}
                            sx={{ cursor: 'pointer', '&:hover': { color: '#dc2626' } }}
                            onClick={() => {
                              setVoters(null);
                              setVotersDialog({ entryId: entry.id, question: entry.question });
                              setVotersLoading(true);
                              api.faq.getFeedbackVoters(entry.id)
                                .then(v => setVoters(v))
                                .finally(() => setVotersLoading(false));
                            }}
                          >
                            <ThumbDownAltOutlinedIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                            {entryFeedback[entry.id]?.unhelpful ?? 0}
                          </Box>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {entry.createdBy.split('/').pop()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(entry.updatedAt).toLocaleDateString('en-GB')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setEditingEntry(entry); setEntryFormOpen(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({
                            type: 'entry', id: entry.id, name: entry.question,
                          })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {entries.length === 0 && (
              <Box p={3} textAlign="center" color="text.secondary">No entries yet.</Box>
            )}
          </Paper>
        </>
      );
    }

    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Page themeId="tool">
      <Header title="FAQs — Admin" subtitle="Manage sections, categories and FAQ entries" />
      <Content>
        <Breadcrumbs />

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : renderLevelContent()}

        {/* ── Dialogs ── */}
        <SectionForm
          open={sectionFormOpen}
          section={editingSection}
          onClose={() => setSectionFormOpen(false)}
          onSave={handleSaveSection}
        />

        <CategoryForm
          open={categoryFormOpen}
          category={editingCategory}
          sectionName={selectedSection?.name ?? ''}
          onClose={() => setCategoryFormOpen(false)}
          onSave={handleSaveCategory}
        />

        <EntryForm
          open={entryFormOpen}
          entry={editingEntry}
          sectionName={selectedSection?.name ?? ''}
          categoryName={selectedCategory?.name ?? ''}
          onClose={() => setEntryFormOpen(false)}
          onSave={handleSaveEntry}
        />

        {/* Delete confirmation */}
        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm delete</DialogTitle>
          <DialogContent>
            <Typography>
              Delete <strong>&ldquo;{deleteDialog?.name}&rdquo;</strong>?
            </Typography>
            {deleteDialog?.warning && (
              <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                {deleteDialog.warning}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Feedback voters */}
        <Dialog open={!!votersDialog} onClose={() => setVotersDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Feedback voters</DialogTitle>
          <DialogContent sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {votersDialog && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                &ldquo;{votersDialog.question}&rdquo;
              </Typography>
            )}
            {votersLoading && (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!votersLoading && voters && (
              <Box display="flex" gap={3} mt={1} flexWrap="wrap">
                <Box flex={1} minWidth={160}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <ThumbUpAltOutlinedIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                    <Typography variant="subtitle2">Helpful ({voters.helpful.length})</Typography>
                  </Box>
                  {voters.helpful.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No votes yet</Typography>
                  ) : (
                    voters.helpful.map(v => (
                      <Box key={v.userRef} display="flex" justifyContent="space-between" gap={2} py={0.25}>
                        <Typography variant="body2">{v.userRef.split('/').pop()}</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(v.votedAt).toLocaleDateString('en-GB')}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                <Box flex={1} minWidth={160}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <ThumbDownAltOutlinedIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                    <Typography variant="subtitle2">Not helpful ({voters.unhelpful.length})</Typography>
                  </Box>
                  {voters.unhelpful.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No votes yet</Typography>
                  ) : (
                    voters.unhelpful.map(v => (
                      <Box key={v.userRef} display="flex" justifyContent="space-between" gap={2} py={0.25}>
                        <Typography variant="body2">{v.userRef.split('/').pop()}</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(v.votedAt).toLocaleDateString('en-GB')}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVotersDialog(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
}
